import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';
interface ScrollSwapLineProps {
  /** Line shown first */
  from: string;
  /** Line it blurs over to once scrolled past `swapAt` */
  to: string;
  /** Swap once the line's top edge rises to this fraction of the viewport height (default: middle) */
  swapAt?: number;
  /** Phones: swap point override (smaller = later, higher up the screen) */
  mobileSwapAt?: number;
  /** Phones: break the `to` line onto a second line after this word index */
  mobileBreakAfter?: number;
  /** Phones: break the `from` line onto a second line after this word index */
  mobileBreakFromAfter?: number;
  /**
   * "blend": white with mix-blend-difference (inverts over whatever is behind — for lines that cross
   * colour changes). "dark": plain solid black, for lines that only ever sit on white. Use "dark" where
   * possible: mobile Safari drops the blend while the words animate their blur, which turned the line
   * white-on-white mid-swap so it seemed to vanish.
   */
  tone?: 'blend' | 'dark';
}
const SWITCH_EASE = [0.45, 0, 0.25, 1] as const;
/** Per-word delay for the faint left-to-right ripple (s) */
const WORD_STAGGER = 0.035;
/** Swapping back needs the line to drop this far (fraction of the screen) below the swap point */
const HYSTERESIS = 0.08;

/**
 * Centered serif line in the manifesto style ("Most people build brands…"). Comes into focus on scroll,
 * inverts against whatever is behind it, and cross-blurs to a second line once scrolled past `swapAt`
 * (and back again when scrolling up).
 *
 * Both lines stay mounted in one grid cell and simply animate in/out — nothing is ever unmounted, so the
 * line can't vanish mid-swap, and the cell keeps the taller line's height so nothing below jumps.
 */
export const ScrollSwapLine = forwardRef<HTMLParagraphElement, ScrollSwapLineProps>(function ScrollSwapLine({
  from,
  to,
  swapAt = 0.5,
  mobileSwapAt,
  mobileBreakAfter,
  mobileBreakFromAfter,
  tone = 'blend'
}, ref) {
  const reduce = useReducedMotion();
  const lineRef = useRef<HTMLParagraphElement>(null);
  useImperativeHandle(ref, () => lineRef.current as HTMLParagraphElement);
  const [swapped, setSwapped] = useState(false);
  const mobile = useIsMobile();
  useEffect(() => {
    let raf = 0;
    // A stable screen height: phone address bars grow/shrink while scrolling and change innerHeight,
    // which used to slide the swap point under the line and flip it back and forth. Only a real
    // width change (rotation, desktop resize) re-measures it.
    let width = window.innerWidth;
    let vh = window.innerHeight;
    const check = () => {
      raf = 0;
      const el = lineRef.current;
      if (!el) return;
      const at = mobileSwapAt !== undefined && window.innerWidth < 768 ? mobileSwapAt : swapAt;
      const top = el.getBoundingClientRect().top;
      setSwapped(s => {
        // Hysteresis: swap at `at`, but only swap back once clearly below it
        const next = s ? top <= vh * (at + HYSTERESIS) : top <= vh * at;
        return s === next ? s : next;
      });
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    const onResize = () => {
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        vh = window.innerHeight;
      }
      schedule();
    };
    schedule();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', onResize);
    };
  }, [swapAt, mobileSwapAt]);

  // Phones: the outgoing line clears first, then the new one resolves — they wrap differently there,
  // so overlapping them was unreadable. Desktop keeps the cross-blur.
  const outDuration = mobile ? 0.3 : 0.7;
  const inDelay = mobile ? 0.3 : 0;
  return <motion.p ref={lineRef}
  // Both lines share one grid cell, so they blur through each other in place (no empty gap)
  className={`grid justify-items-center px-3 text-center font-serif font-medium md:font-normal text-[1.375rem] md:text-[clamp(1.125rem,calc(1.75*var(--vw)),1.75rem)] leading-snug tracking-[-0.01em] ${tone === 'dark' ? 'text-black' : 'text-white mix-blend-difference'}`}
  // Visible-from-the-start fallback on the wrapper: only opacity animates here (no filter), so the
  // wrapper can never get stuck blurred or hidden while the words below run their own swap
  {...reduce ? {} : {
    initial: {
      opacity: 0,
      y: 10
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.5
    },
    transition: {
      duration: 0.9,
      ease: SWITCH_EASE
    }
  }} data-id="element-268" data-mp="rru5fvc2090u">
      <span className="sr-only" aria-live="polite" data-id="element-269" data-mp="1pboko709soq4">
        {swapped ? to : from}
      </span>
      {[from, to].map((line, li) => {
      const active = li === 1 === swapped;
      const breakAt = li === 1 ? mobileBreakAfter : mobileBreakFromAfter;
      const words = line.split(' ');
      return <span key={li} aria-hidden="true" className="[grid-area:1/1]" style={{
        pointerEvents: active ? 'auto' : 'none'
      }} data-id="element-270" data-mp="240ggfam9pr9o" data-mpi={li}>
            {words.map((word, i) =>
        // Old line drifts out toward the top-right; the new one resolves in from the bottom-left, word by word
        <motion.span key={`${word}-${i}`} className="inline-block whitespace-pre" initial={false} animate={active ? {
          opacity: 1,
          x: 0,
          y: 0,
          filter: 'blur(0px)',
          transitionEnd: {
            filter: 'none'
          }
        } : reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          x: 4,
          y: -4,
          filter: 'blur(6px)'
        }} transition={reduce ? {
          duration: 0
        } : active ? {
          duration: 0.8,
          ease: SWITCH_EASE,
          delay: inDelay + i * WORD_STAGGER
        } : {
          duration: outDuration,
          ease: SWITCH_EASE
        }} data-id="element-271" data-mp="1gavymv1up01z" data-mpi={i}>
                  {i < words.length - 1 ? `${word} ` : word}
                </motion.span>).flatMap((node, i) =>
        // Phones: deliberate line breaks, so each line splits into two well-balanced halves
        i === breakAt ? [node, <br key="mobile-break" className="md:hidden" data-id="element-272" data-mp="3n0z65hilw1vt" />] : [node])}
          </span>;
    })}
    </motion.p>;
});